
import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { supabase } from "../lib/supabase";

interface User {
  id: string;
  userId: string;
  name: string;
  email: string;
  avatar?: string;
  role: "student" | "admin";
}

interface LoginResult {
  success: boolean;
  error?: string;
}

interface AuthContextType {
  user: User | null;
  loginAdmin: (email: string, password: string) => Promise<LoginResult>;
  loginAluno: (email: string, password: string) => Promise<LoginResult>;
  registerAluno: (
    name: string,
    email: string,
    password: string,
  ) => Promise<LoginResult>;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isStudent: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // -------------------------------------------------------------------------
  // Auth state handling
  // -------------------------------------------------------------------------
  useEffect(() => {
    checkUser();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        loadUserData(session.user.id);
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // -------------------------------------------------------------------------
  // Helper: verify if there is an authenticated session
  // -------------------------------------------------------------------------
  const checkUser = async () => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session?.user) {
        await loadUserData(session.user.id);
      }
    } catch (error) {
      console.error("Erro ao verificar usuário:", error);
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Helper: load user profile from either "administradores" or "alunos"
  // -------------------------------------------------------------------------
  const loadUserData = async (authUserId: string) => {
    try {
      // Check admin table
      const { data: adminData, error: adminError } = await supabase
        .from("administradores")
        .select("*")
        .eq("user_id", authUserId)
        .maybeSingle();

      if (adminError) {
        console.error("Erro ao buscar admin:", adminError);
      }

      if (adminData) {
        setUser({
          id: adminData.id,
          userId: adminData.user_id,
          name: adminData.nome,
          email: adminData.email,
          role: "admin",
        });
        setLoading(false);
        return;
      }

      // Check student table
      const { data: alunoData, error: alunoError } = await supabase
        .from("alunos")
        .select("*")
        .eq("user_id", authUserId)
        .maybeSingle();

      if (alunoError) {
        console.error("Erro ao buscar aluno:", alunoError);
      }

      if (alunoData) {
        setUser({
          id: alunoData.id,
          userId: alunoData.user_id,
          name: alunoData.nome,
          email: alunoData.email,
          role: "student",
        });
        setLoading(false);
        return;
      }

      // Authenticated but not registered in any table
      setUser(null);
      setLoading(false);
    } catch (error) {
      console.error("Erro ao carregar dados do usuário:", error);
      setUser(null);
      setLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Admin login
  // -------------------------------------------------------------------------
  const loginAdmin = async (
    email: string,
    password: string,
  ): Promise<LoginResult> => {
    try {
      // Step 1: Authenticate with Supabase Auth
      const { data: authData, error: authError } =
        await supabase.auth.signInWithPassword({
          email,
          password,
        });

      if (authError) {
        console.error("Erro de autenticação:", authError);
        return { success: false, error: "Email ou senha incorretos" };
      }

      if (!authData.user) {
        return { success: false, error: "Erro ao autenticar" };
      }

      console.log("✅ Usuário autenticado:", authData.user.id);

      // Step 2: Check if user exists in administradores table
      const { data: adminData, error: adminError } = await supabase
        .from("administradores")
        .select("*")
        .eq("user_id", authData.user.id)
        .maybeSingle();

      console.log("🔍 Buscando admin com user_id:", authData.user.id);
      console.log("📊 Resultado da busca:", { adminData, adminError });

      if (adminError) {
        console.error("❌ Erro ao buscar administrador:", adminError);
        await supabase.auth.signOut();
        return {
          success: false,
          error: "Erro ao verificar permissões de administrador",
        };
      }

      if (!adminData) {
        console.warn("⚠️ Usuário não encontrado na tabela administradores");
        await supabase.auth.signOut();
        return {
          success: false,
          error: "Você não tem permissão de administrador",
        };
      }

      console.log("✅ Administrador encontrado:", adminData);

      // Step 3: Set user state
      setUser({
        id: adminData.id,
        userId: adminData.user_id,
        name: adminData.nome,
        email: adminData.email,
        role: "admin",
      });

      return { success: true };
    } catch (error) {
      console.error("💥 Erro no login admin:", error);
      return { success: false, error: "Erro ao fazer login" };
    }
  };

  // -------------------------------------------------------------------------
  // Student login
  // -------------------------------------------------------------------------
  const loginAluno = async (
    email: string,
    password: string,
  ): Promise<LoginResult> => {
    try {
      const { data: authData, error: authError } =
        await supabase.auth.signInWithPassword({
          email,
          password,
        });

      if (authError) {
        return { success: false, error: "Email ou senha incorretos" };
      }

      if (!authData.user) {
        return { success: false, error: "Erro ao autenticar" };
      }

      const { data: alunoData, error: alunoError } = await supabase
        .from("alunos")
        .select("*")
        .eq("user_id", authData.user.id)
        .maybeSingle();

      if (alunoError || !alunoData) {
        await supabase.auth.signOut();
        return { success: false, error: "Conta de aluno não encontrada" };
      }

      if (alunoData.status === "bloqueado") {
        await supabase.auth.signOut();
        return {
          success: false,
          error:
            "Sua conta está bloqueada. Entre em contato com o suporte.",
        };
      }

      setUser({
        id: alunoData.id,
        userId: alunoData.user_id,
        name: alunoData.nome,
        email: alunoData.email,
        role: "student",
      });

      return { success: true };
    } catch (error) {
      console.error("Erro no login aluno:", error);
      return { success: false, error: "Erro ao fazer login" };
    }
  };

  // -------------------------------------------------------------------------
  // Student registration
  // -------------------------------------------------------------------------
  const registerAluno = async (
    name: string,
    email: string,
    password: string,
  ): Promise<LoginResult> => {
    try {
      // Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
      });

      if (authError) {
        if (authError.message.includes("already registered")) {
          return { success: false, error: "Este email já está cadastrado" };
        }
        return { success: false, error: authError.message };
      }

      if (!authData.user) {
        return { success: false, error: "Erro ao criar conta" };
      }

      // Insert into alunos table
      const { error: alunoError } = await supabase
        .from("alunos")
        .insert({
          user_id: authData.user.id,
          nome: name,
          email: email,
          status: "ativo",
        });

      if (alunoError) {
        console.error("Erro ao criar aluno:", alunoError);
        return { success: false, error: "Erro ao finalizar cadastro" };
      }

      // Retrieve newly created record
      const { data: alunoData } = await supabase
        .from("alunos")
        .select("*")
        .eq("user_id", authData.user.id)
        .maybeSingle();

      if (alunoData) {
        setUser({
          id: alunoData.id,
          userId: alunoData.user_id,
          name: alunoData.nome,
          email: alunoData.email,
          role: "student",
        });
      }

      return { success: true };
    } catch (error) {
      console.error("Erro no registro:", error);
      return { success: false, error: "Erro ao criar conta" };
    }
  };

  // -------------------------------------------------------------------------
  // Logout
  // -------------------------------------------------------------------------
  const logout = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
    } catch (error) {
      console.error("Erro ao fazer logout:", error);
    }
  };

  // -------------------------------------------------------------------------
  // Provider render
  // -------------------------------------------------------------------------
  return (
    <AuthContext.Provider
      value={{
        user,
        loginAdmin,
        loginAluno,
        registerAluno,
        logout,
        isAuthenticated: !!user,
        isAdmin: user?.role === "admin",
        isStudent: user?.role === "student",
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// -------------------------------------------------------------------------
// Hook for consuming the context
// -------------------------------------------------------------------------
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
